import { Eye, EyeOff } from "lucide-react";

import { DataTable, type DataTableColumn } from "@/components/cinch/DataTable/DataTable";
import { EntityChain } from "@/components/cinch/EntityChain";
import { EntityDrawer } from "@/components/cinch/EntityDrawer";
import { EntityName } from "@/components/cinch/EntityName";
import { Button } from "@/components/ui/button";
import { useCan } from "@/lib/auth";

import {
  useAddWatch,
  useChildren,
  useEntityIdentity,
  useRemoveWatch,
  useTimeseries,
  useWatchlist,
  type ChildRow,
} from "../api";
import { LEVEL_LABEL, entityChain, entitySubject } from "../display";
import { DualClockChart } from "./DualClockChart";

/** The level walk (decision #9): any L1/L2/L3 key opens here. Charts serve
 *  local-first from daily_counts (instant for tracked keys) with a live
 *  Snowflake fallback; the children table walks Integration → Companies →
 *  Locations — click a child to drill one level down. */
export function EntityQuickDrawer({
  level,
  keyPath,
  expanded,
  onExpandedChange,
  onClose,
  onOpenEntity,
}: {
  level: string | null;
  keyPath: string | null;
  expanded: boolean;
  onExpandedChange: (v: boolean) => void;
  onClose: () => void;
  onOpenEntity: (level: string, keyPath: string) => void;
}) {
  const canWrite = useCan("integrations.write");
  const ts = useTimeseries(level, keyPath, expanded ? 180 : 60);
  const children = useChildren(level, keyPath);
  const identity = useEntityIdentity(level, keyPath);
  const watchlist = useWatchlist();
  const addWatch = useAddWatch();
  const removeWatch = useRemoveWatch();
  const watch = watchlist.data?.find((w) => w.level === level && w.key_path === keyPath);

  const childLevel = level === "L1" ? "L2" : "L3";
  const childColumns: DataTableColumn<ChildRow>[] = [
    {
      key: "name",
      header: LEVEL_LABEL[childLevel] ?? "Child",
      render: (r) => (
        <EntityName name={r.name} fallback={r.child_id} context={r.child_id} />
      ),
    },
    {
      key: "volume_30d",
      header: "30d volume",
      numeric: true,
      render: (r) => r.volume_30d.toLocaleString(),
    },
    {
      key: "recent_rate",
      header: "Trend /day",
      numeric: true,
      render: (r) =>
        r.prior_rate != null && r.recent_rate != null
          ? `${r.prior_rate} → ${r.recent_rate}`
          : "—",
    },
  ];

  return (
    <EntityDrawer
      open={keyPath != null}
      onOpenChange={(v) => {
        if (!v && !expanded) onClose();
      }}
      expanded={expanded}
      onExpandedChange={onExpandedChange}
      onClose={onClose}
      title={
        identity.data
          ? (entitySubject(identity.data).name ?? entitySubject(identity.data).id)
          : (keyPath ?? "Entity")
      }
      subtitle={level ? (LEVEL_LABEL[level] ?? level) : ""}
    >
      {keyPath != null && level != null && (
        <div className="space-y-4 p-4">
          {/* The chain answers "where am I?" — the title alone cannot, because
              a location name repeats across companies. Ancestors drill. */}
          {identity.data && (
            <EntityChain
              links={entityChain(identity.data).map((l) => ({
                id: l.id,
                label: l.levelLabel,
                name: l.name,
                current: l.isSubject,
              }))}
              onLinkClick={(_l, i) => {
                const link = entityChain(identity.data!)[i];
                if (link) onOpenEntity(link.level, link.keyPath);
              }}
            />
          )}
          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground">
              {LEVEL_LABEL[level]} view — tracked keys chart instantly from the
              local store; anything else computes live from Snowflake.
            </p>
            {canWrite && (
              <Button
                variant="outline"
                size="sm"
                disabled={addWatch.isPending || removeWatch.isPending}
                onClick={() =>
                  watch
                    ? removeWatch.mutate(watch.id)
                    : addWatch.mutate({ level, key_path: keyPath })
                }
              >
                {watch ? (
                  <>
                    <EyeOff className="mr-1 h-3.5 w-3.5" /> Unwatch
                  </>
                ) : (
                  <>
                    <Eye className="mr-1 h-3.5 w-3.5" /> Watch
                  </>
                )}
              </Button>
            )}
          </div>
          {ts.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading volume history…</p>
          ) : (
            <DualClockChart points={ts.data?.points ?? []} />
          )}

          {(level === "L1" || level === "L2") && (
            <div>
              <h3 className="mb-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                {level === "L1" ? "Companies on this integration" : "Locations in this company"}
              </h3>
              <DataTable
                columns={childColumns}
                rows={children.data ?? []}
                rowKey={(r) => r.key_path}
                loading={children.isLoading}
                onRowClick={(r) => onOpenEntity(r.level, r.key_path)}
                emptyMessage={
                  children.isLoading ? "Loading…" : "No activity in the last 60 days."
                }
                containerClassName="max-h-96"
              />
            </div>
          )}
        </div>
      )}
    </EntityDrawer>
  );
}
